"use client";

import Link from "next/link";
import { useState } from "react";

import {
  Button,
  Input,
  NativeSelect,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@stocks/ui";
import {
  PURCHASE_ORDER_STATUS,
  PURCHASE_ORDER_STATUS_VALUES,
  type ListPurchaseOrdersQuery,
  type PurchaseOrderListItem,
  type PurchaseOrderStatus,
} from "@stocks/shared";

import { usePaginatedList } from "@/shared/libs/query";
import {
  ORDER_STATUS_LABELS,
  OrderStatusBadge,
  formatDate,
  formatOrderQuantity,
  orderKeys,
  useCustomerOptions,
} from "@/entities/order";
import { CancelOrderButton } from "@/features/order/cancel-order";

const PAGE_SIZE = 20;
const COLS = 8;

/** 발주 목록 — 고객별·상태별 조회, 상세/수정/취소 진입 */
export const OrdersTable = () => {
  const [page, setPage] = useState(1);
  const [keyword, setKeyword] = useState("");
  const [search, setSearch] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [status, setStatus] = useState<PurchaseOrderStatus | "">("");

  const query: ListPurchaseOrdersQuery = {
    page,
    pageSize: PAGE_SIZE,
    sort: "createdAt",
    order: "desc",
    search: search || undefined,
    customerId: customerId || undefined,
    status: status || undefined,
  };
  const { data, isLoading, isError } = usePaginatedList<
    PurchaseOrderListItem,
    ListPurchaseOrdersQuery
  >(orderKeys.orders, "/v1/purchase-orders", query);
  const { data: customers } = useCustomerOptions();

  return (
    <div className='flex flex-col gap-4'>
      <form
        className='flex flex-wrap gap-2'
        onSubmit={(e) => {
          e.preventDefault();
          setSearch(keyword.trim());
          setPage(1);
        }}
      >
        <Input
          className='w-64'
          placeholder='발주번호 / 고객명 검색'
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
        />
        <Button type='submit'>검색</Button>
        <NativeSelect
          aria-label='고객 필터'
          value={customerId}
          onChange={(e) => {
            setCustomerId(e.target.value);
            setPage(1);
          }}
        >
          <option value=''>전체 고객</option>
          {customers?.items.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </NativeSelect>
        <NativeSelect
          aria-label='상태 필터'
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as PurchaseOrderStatus | "");
            setPage(1);
          }}
        >
          <option value=''>전체 상태</option>
          {PURCHASE_ORDER_STATUS_VALUES.map((s) => (
            <option key={s} value={s}>
              {ORDER_STATUS_LABELS[s]}
            </option>
          ))}
        </NativeSelect>
      </form>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>발주번호</TableHead>
            <TableHead>고객</TableHead>
            <TableHead>납기일</TableHead>
            <TableHead className='text-right'>제품 행</TableHead>
            <TableHead className='text-right'>총 수량</TableHead>
            <TableHead>상태</TableHead>
            <TableHead>등록일</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading && (
            <TableRow>
              <TableCell colSpan={COLS} className='text-center text-muted-foreground'>
                불러오는 중…
              </TableCell>
            </TableRow>
          )}
          {isError && (
            <TableRow>
              <TableCell colSpan={COLS} className='text-center text-destructive'>
                발주를 불러오지 못했습니다.
              </TableCell>
            </TableRow>
          )}
          {data?.items.length === 0 && !isLoading && (
            <TableRow>
              <TableCell colSpan={COLS} className='text-center text-muted-foreground'>
                조건에 맞는 발주가 없습니다.
              </TableCell>
            </TableRow>
          )}
          {data?.items.map((o) => (
            <TableRow key={o.id}>
              <TableCell>
                <Link href={`/orders/${o.id}`} className='font-medium hover:underline'>
                  {o.orderNo}
                </Link>
              </TableCell>
              <TableCell>{o.customerName}</TableCell>
              <TableCell>{formatDate(o.dueDate)}</TableCell>
              <TableCell className='text-right'>{o.lineCount}</TableCell>
              <TableCell className='text-right'>
                {formatOrderQuantity(o.totalQuantity)}
              </TableCell>
              <TableCell>
                <OrderStatusBadge status={o.status} />
              </TableCell>
              <TableCell>{formatDate(o.createdAt)}</TableCell>
              <TableCell>
                {o.status === PURCHASE_ORDER_STATUS.OPEN && (
                  <div className='flex gap-2'>
                    <Button asChild size='sm' variant='outline'>
                      <Link href={`/orders/${o.id}/edit`}>수정</Link>
                    </Button>
                    <CancelOrderButton orderId={o.id} orderNo={o.orderNo} />
                  </div>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {data && (
        <div className='flex items-center justify-end gap-3 text-sm'>
          <span className='text-muted-foreground'>총 {data.meta.total}건</span>
          <Button
            variant='outline'
            size='sm'
            disabled={!data.meta.hasPrev}
            onClick={() => setPage((p) => p - 1)}
          >
            이전
          </Button>
          <span>
            {data.meta.page} / {data.meta.totalPages}
          </span>
          <Button
            variant='outline'
            size='sm'
            disabled={!data.meta.hasNext}
            onClick={() => setPage((p) => p + 1)}
          >
            다음
          </Button>
        </div>
      )}
    </div>
  );
};
